import { useCallback, useEffect } from 'react';

// Map routes to their dynamic import functions
const routeImports: Record<string, () => Promise<unknown>> = {
  '/styles': () => import('@/pages/Styles'),
  '/tools': () => import('@/pages/Tools'),
  '/platforms': () => import('@/pages/Platforms'),
  '/custom-style': () => import('@/pages/CustomStyleCreator'),
  '/instagram-fonts': () => import('@/pages/InstagramGenerator'),
  '/tiktok-fonts': () => import('@/pages/TikTokGenerator'),
  '/discord-fonts': () => import('@/pages/DiscordGenerator'),
  '/twitter-fonts': () => import('@/pages/TwitterGenerator'),
  '/facebook-fonts': () => import('@/pages/FacebookGenerator'),
  '/linkedin-fonts': () => import('@/pages/LinkedInGenerator'),
  '/youtube-fonts': () => import('@/pages/YouTubeGenerator'),
  '/tools/word-counter': () => import('@/pages/WordCounterPage'),
  '/tools/hashtag-generator': () => import('@/pages/HashtagGeneratorPage'),
  '/tools/social-preview': () => import('@/pages/SocialPreviewPage'),
  '/tools/batch-transform': () => import('@/pages/BatchTransformPage'),
  '/tools/case-converter': () => import('@/pages/CaseConverterPage'),
  '/tools/invisible-text': () => import('@/pages/InvisibleTextPage'),
  '/tools/unicode-lookup': () => import('@/pages/UnicodeLookupPage'),
  '/tools/qr-code': () => import('@/pages/QRCodePage'),
  '/about': () => import('@/pages/About'),
};

// Track which routes have been prefetched to avoid duplicate requests
const prefetchedRoutes = new Set<string>();

/**
 * Prefetch a route's component
 */
export function prefetchRoute(path: string): void {
  // Skip if already prefetched or no import defined
  if (prefetchedRoutes.has(path) || !routeImports[path]) {
    return;
  }

  prefetchedRoutes.add(path);
  
  // Use requestIdleCallback for non-blocking prefetch, with setTimeout fallback
  const prefetch = () => {
    routeImports[path]?.().catch(() => {
      // Remove from set so it can be retried
      prefetchedRoutes.delete(path);
    });
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(prefetch, { timeout: 2000 });
  } else {
    setTimeout(prefetch, 100);
  }
}

/**
 * Prefetch multiple routes at once
 */
export function prefetchRoutes(paths: string[]): void {
  paths.forEach(prefetchRoute);
}

/**
 * Hook to prefetch critical routes when component mounts
 */
export function usePrefetchOnMount(paths: string[]): void {
  useEffect(() => {
    // Delay prefetch slightly to not interfere with initial render
    const timer = setTimeout(() => {
      prefetchRoutes(paths);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);
}

/**
 * Hook that returns handlers for hover-based prefetching
 */
export function usePrefetchOnHover(path: string) {
  const onMouseEnter = useCallback(() => {
    prefetchRoute(path);
  }, [path]);

  const onFocus = useCallback(() => {
    prefetchRoute(path);
  }, [path]);

  return { onMouseEnter, onFocus };
}
